import { Check, Lock, Mail, ShieldCheck, X } from 'lucide-react';
import * as React from 'react';

import {
  Alert, AlertDescription, Badge, Button, Card, CardContent, Input, Label,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Skeleton, Switch,
  Textarea,
} from '@/components/cargofl-ui';
import { ConfirmButton } from '@/components/confirm-dialog';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { ApiError } from '@/lib/api';
import {
  useSaveSettings, useSystemInfo, useTestChat, type EnvironmentInfo, type Settings,
} from '@/lib/settings-api';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * What the system does, and what it is running on.
 *
 * Split in two on purpose. The top half is settings somebody may change, and each
 * one takes effect on the next request without a deploy. The bottom half is fixed
 * at deploy time and is here to be READ - so nobody has to open a shell to answer
 * "is mail on?" or "which domain can sign in?".
 *
 * Credentials are reported as configured or not and never shown. An SMTP URL
 * contains its password, and a settings page is exactly where somebody would be
 * talked into reading one out.
 */
export function SystemPage() {
  const { data, isPending } = useSystemInfo();
  const save = useSaveSettings();
  const [draft, setDraft] = React.useState<Settings | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const settings = draft ?? data?.settings ?? null;
  const dirty =
    draft !== null && data !== undefined && JSON.stringify(draft) !== JSON.stringify(data.settings);

  const set = <K extends keyof Settings>(key: K, value: Settings[K]) => {
    if (!settings) return;
    setNotice(null);
    setDraft({ ...settings, [key]: value });
  };

  const apply = async () => {
    if (!draft) return;
    setError(null);
    try {
      await save.mutateAsync(draft);
      setDraft(null);
      setNotice('Saved. The change applies from the next request.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save those settings.');
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="System"
        description="How the system behaves, and what it is running on."
      />

      {isPending && <Skeleton className="h-96 w-full" />}

      {data && settings && (
        <>
          {/* ------------------------------------------------ what it does -- */}
          <Card>
            <CardContent className="space-y-6 py-5">
              <Section title="Recording">
                <Field
                  label="Duration steps"
                  hint="Hours are recorded in whole steps of this size. Applies to the day form, the weekly grid, the CSV import and working contracts."
                >
                  <Select
                    value={String(settings.durationIncrementMinutes)}
                    onValueChange={(v) =>
                      set('durationIncrementMinutes', Number(v) as Settings['durationIncrementMinutes'])
                    }
                  >
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[1, 5, 15, 30, 60].map((m) => (
                        <SelectItem key={m} value={String(m)}>
                          {m} minute{m === 1 ? '' : 's'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field
                  label="Off-step durations"
                  hint="What happens when somebody enters 20 minutes against a 15-minute step. Refusing says so and lets them correct it; the others change the figure silently and save the changed one."
                >
                  <Select
                    value={settings.durationRounding}
                    onValueChange={(v) =>
                      set('durationRounding', v as Settings['durationRounding'])
                    }
                  >
                    <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Refuse them</SelectItem>
                      <SelectItem value="nearest">Round to the nearest step</SelectItem>
                      <SelectItem value="up">Round up</SelectItem>
                      <SelectItem value="down">Round down</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>

                <Field
                  label="Default start time"
                  hint="The clock time a new entry starts at when nobody gives one. Only affects how an entry reads, not its duration."
                >
                  <Input
                    type="time"
                    className="w-40"
                    value={settings.defaultBeginTime}
                    onChange={(e) => set('defaultBeginTime', e.target.value)}
                  />
                </Field>

                <Field
                  label="Close days automatically"
                  hint={
                    settings.lockdownDefaultEnd === 'yesterday'
                      ? 'Each day closes once it has passed, for anyone without their own boundary from an approved week.'
                      : 'Nothing closes automatically. Only submitting or approving a week locks it.'
                  }
                >
                  <Select
                    value={settings.lockdownDefaultEnd}
                    onValueChange={(v) => set('lockdownDefaultEnd', v as Settings['lockdownDefaultEnd'])}
                  >
                    <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yesterday">At the end of each day</SelectItem>
                      <SelectItem value="none">Never — leave history open</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>

                <Toggle
                  label="Prevent overbooking a budget"
                  hint={
                    settings.allowOverbookingBudget
                      ? 'Budgets are advisory: an entry that would exceed one is still accepted. This is how the previous system ran.'
                      : 'An entry that would take a customer, project or activity past its budget is refused, naming the budget and what is left.'
                  }
                  checked={!settings.allowOverbookingBudget}
                  onChange={(v) => set('allowOverbookingBudget', !v)}
                />

                <Field
                  label="Bill an hour as"
                  hint="'As recorded' bills the exact duration. 'Rounded to 1/100 hour' rounds it first, so 1h 0m 30s bills as 1.01 hours. The previous system ran on the first."
                >
                  <Select
                    value={settings.rateMode}
                    onValueChange={(v) => set('rateMode', v as Settings['rateMode'])}
                  >
                    <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="classic">As recorded</SelectItem>
                      <SelectItem value="decimal">Rounded to 1/100 hour</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>

              <Field
                  label="Financial year starts in"
                  hint="The year reports - the year grid and the overtime balance - run twelve months from this one. April here means 2026-27 runs April 2026 to March 2027. Set it to January for a calendar year."
                >
                  <Select
                    value={String(settings.financialYearStartMonth)}
                    onValueChange={(v) => set('financialYearStartMonth', Number(v))}
                  >
                    <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTH_NAMES.map((name, i) => (
                        <SelectItem key={name} value={String(i + 1)}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

              <Field
                  label="Count overtime from"
                  hint="Days before this are not counted towards anybody's balance. Without it, a year report counts every day of it as expected-but-not-worked for however long the system was not yet in use. Leave empty to count whole years."
                >
                  <Input
                    type="date"
                    className="w-44"
                    value={settings.overtimeStartDate}
                    onChange={(e) => set('overtimeStartDate', e.target.value)}
                  />
                </Field>
              </Section>

              <Section title="Notifications">
                <Toggle
                  label="Email about leave"
                  hint="A request goes to the person's supervisor; a decision goes back to them."
                  checked={settings.notifyAbsence}
                  disabled={!data.environment.mailConfigured}
                  onChange={(v) => set('notifyAbsence', v)}
                />
                <Toggle
                  label="Email about timesheet weeks"
                  hint="A submission goes to the approver; an approval, a send-back or a reopen goes back to the person."
                  checked={settings.notifyTimesheet}
                  disabled={!data.environment.mailConfigured}
                  onChange={(v) => set('notifyTimesheet', v)}
                />
                {!data.environment.mailConfigured && (
                  <p className="text-xs text-muted-foreground">
                    No mail server is configured, so nothing is sent regardless of these.
                  </p>
                )}
              </Section>

              <ChatSection settings={settings} set={set} />

              <Section title="Numbering">
                <p className="max-w-prose text-xs leading-relaxed text-muted-foreground">
                  How a new customer, project or activity gets its reference.{' '}
                  <code className="font-mono">{'{cc}'}</code>,{' '}
                  <code className="font-mono">{'{pc}'}</code> and{' '}
                  <code className="font-mono">{'{ac}'}</code> count what already
                  exists; <code className="font-mono">{'{Y}'}</code>{' '}
                  <code className="font-mono">{'{M}'}</code>{' '}
                  <code className="font-mono">{'{D}'}</code> are the date;{' '}
                  <code className="font-mono">,4</code> pads to four digits. Leave one
                  empty and that kind is simply created without a number.
                </p>
                <Field label="Customer number" hint="Every migrated customer has one, reaching 0058.">
                  <Input
                    className="w-40 font-mono"
                    value={settings.customerNumberFormat}
                    onChange={(e) => set('customerNumberFormat', e.target.value)}
                  />
                </Field>
                <Field label="Project number" hint="Every migrated project has one, reaching 0062.">
                  <Input
                    className="w-40 font-mono"
                    value={settings.projectNumberFormat}
                    onChange={(e) => set('projectNumberFormat', e.target.value)}
                  />
                </Field>
                <Field
                  label="Activity number"
                  hint="All 117 migrated activities carry one, so without a format here every activity created since would have been the only unnumbered row in the table."
                >
                  <Input
                    className="w-40 font-mono"
                    value={settings.activityNumberFormat}
                    onChange={(e) => set('activityNumberFormat', e.target.value)}
                  />
                </Field>
              </Section>

              <Section title="Invoicing">
                <Field
                  label="Invoice number"
                  hint="{Y} {y} {M} {m} {D} {d} for the date, {c} {cy} {cm} {cd} for how many invoices exist in that period, {cc} {ccy} {ccm} {ccd} for the same per customer, {cname} {cnumber} for the customer."
                >
                  <Input
                    className="w-40 font-mono"
                    value={settings.invoiceNumberFormat}
                    onChange={(e) => set('invoiceNumberFormat', e.target.value)}
                  />
                </Field>
                <Field label="Payment due after" hint="Days from the invoice date.">
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={0}
                      max={999}
                      className="w-24"
                      value={settings.invoiceDueDays}
                      onChange={(e) => set('invoiceDueDays', Number(e.target.value))}
                    />
                    <span className="text-sm text-muted-foreground">days</span>
                  </div>
                </Field>
                <Field
                  label="Tax rate"
                  hint="Added to every invoice. Zero adds no tax line at all. Held as basis points, so 2.5% stays exact rather than becoming 2.4999 in a float."
                >
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min={0}
                      max={99.99}
                      step={0.01}
                      className="w-24"
                      value={settings.invoiceTaxBasisPoints / 100}
                      onChange={(e) =>
                        // Through basis points, so what is stored is an integer and
                        // 18 -> 1800 exactly rather than 1799.9999.
                        set('invoiceTaxBasisPoints', Math.round(Number(e.target.value) * 100))
                      }
                    />
                    <span className="text-sm text-muted-foreground">%</span>
                  </div>
                </Field>
              </Section>

              <Section title="Invoice issuer">
                <p className="max-w-prose text-xs leading-relaxed text-muted-foreground">
                  Printed on every invoice. Anything left empty is left off the
                  document rather than printed as a blank label. Without a GSTIN and
                  an account number an Indian B2B invoice is one the client&rsquo;s
                  accounts desk sends back.
                </p>
                <Field label="Address" hint="Under the organisation name at the top of the invoice.">
                  <Textarea
                    rows={3}
                    className="w-full max-w-md"
                    value={settings.invoiceIssuerAddress}
                    onChange={(e) => set('invoiceIssuerAddress', e.target.value)}
                  />
                </Field>
                <Field label="GSTIN" hint="Or whichever tax registration number applies.">
                  <Input
                    className="w-64 font-mono"
                    value={settings.invoiceTaxNumber}
                    onChange={(e) => set('invoiceTaxNumber', e.target.value)}
                  />
                </Field>
                <Field label="Bank account" hint="Account name and number, IFSC, branch — however you want it to read.">
                  <Textarea
                    rows={3}
                    className="w-full max-w-md"
                    value={settings.invoiceBankDetails}
                    onChange={(e) => set('invoiceBankDetails', e.target.value)}
                  />
                </Field>
                <Field label="Terms of payment" hint="Printed below the total.">
                  <Textarea
                    rows={2}
                    className="w-full max-w-md"
                    value={settings.invoicePaymentTerms}
                    onChange={(e) => set('invoicePaymentTerms', e.target.value)}
                  />
                </Field>
              </Section>

              <Section title="Signing in">
                <Toggle
                  label="Allow email and password"
                  hint={
                    data.environment.googleSignInConfigured
                      ? 'Turn this off once everybody has signed in with Google at least once — the People screen shows who has.'
                      : 'Google sign-in is not configured, so this is currently the only way anybody can get in.'
                  }
                  checked={settings.passwordLoginEnabled}
                  disabled={!data.environment.googleSignInConfigured}
                  onChange={(v) => set('passwordLoginEnabled', v)}
                />
                <Field label="Organisation name" hint="Shown on the sign-in screen and in every notification email.">
                  <Input
                    className="w-64"
                    value={settings.organizationName}
                    onChange={(e) => set('organizationName', e.target.value)}
                  />
                </Field>
              </Section>

              {notice && (
                <Alert>
                  <AlertDescription className="text-sm">{notice}</AlertDescription>
                </Alert>
              )}
              {error && (
                <Alert className="border-destructive/30">
                  <AlertDescription className="text-sm">{error}</AlertDescription>
                </Alert>
              )}

              <div className="flex flex-wrap items-center gap-3 border-t pt-4">
                {/* Confirmed, because two of these change who can get in and how far
                    back time can be edited for everybody. */}
                <ConfirmButton
                  disabled={!dirty || save.isPending}
                  pending={save.isPending}
                  title="Apply these settings?"
                  description={
                    <>
                      They take effect for everybody on the next request. Turning off
                      password sign-in removes that route for anyone who has not signed in
                      with Google; changing the duration steps changes what a save will
                      accept from here on, and leaves existing entries alone.
                    </>
                  }
                  confirmLabel="Apply"
                  onConfirm={() => void apply()}
                >
                  {save.isPending ? 'Saving…' : 'Save settings'}
                </ConfirmButton>
                {dirty && (
                  <Button hint={"Throw away the unsaved changes"} variant="ghost" onClick={() => setDraft(null)} disabled={save.isPending}>
                    Discard
                  </Button>
                )}
                {!dirty && <span className="text-xs text-muted-foreground">No unsaved changes.</span>}
              </div>
            </CardContent>
          </Card>

          {/* --------------------------------------------- what it runs on -- */}
          <Environment env={data.environment} counts={data.counts} />
        </>
      )}
    </PageContainer>
  );
}

function Environment({
  env,
  counts,
}: {
  env: EnvironmentInfo;
  counts: { people: number; active: number; entries: number; projects: number; awaitingApproval: number };
}) {
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardContent className="space-y-3 py-5">
          <h3 className="text-sm font-semibold">Deployment</h3>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Fixed when the app was deployed. Changing any of these means an
            environment change and a restart.
          </p>
          <dl className="space-y-2 text-sm">
            <Row label="Time zone" value={env.organizationTimezone} />
            <Row label="Address" value={env.appBaseUrl} />
            <Row label="Mode" value={env.nodeEnv} />
            <Row label="Session length" value={`${env.sessionTtlHours} hours`} />
            <Row label="Sign-in domain" value={env.allowedEmailDomain ?? 'any address'} />
            <Row
              label="Google sign-in"
              value={<Configured on={env.googleSignInConfigured} icon={ShieldCheck} />}
            />
            <Row
              label="Notification email"
              value={
                <span className="flex items-center gap-2">
                  <Configured on={env.mailConfigured} icon={Mail} />
                  {env.mailFrom && (
                    <span className="text-xs text-muted-foreground">from {env.mailFrom}</span>
                  )}
                </span>
              }
            />
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-3 py-5">
          <h3 className="text-sm font-semibold">Contents</h3>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Enough to tell at a glance that the system holds what it should.
          </p>
          <dl className="space-y-2 text-sm">
            <Row label="People" value={`${counts.active} active of ${counts.people}`} />
            <Row label="Projects" value={`${counts.projects} bookable`} />
            <Row label="Recorded entries" value={counts.entries.toLocaleString('en-IN')} />
            <Row
              label="Weeks awaiting approval"
              value={
                <span className={counts.awaitingApproval > 0 ? 'font-semibold' : ''}>
                  {counts.awaitingApproval.toLocaleString('en-IN')}
                </span>
              }
            />
          </dl>
        </CardContent>
      </Card>
    </div>
  );
}

function Configured({ on, icon: Icon }: { on: boolean; icon: React.ComponentType<{ className?: string }> }) {
  return (
    <Badge variant={on ? 'secondary' : 'outline'} className="font-normal">
      <Icon className="size-3" />
      {on ? 'configured' : 'not configured'}
    </Badge>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 border-b pb-2 last:border-0">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="text-sm">{value}</dd>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold">
        <Lock className="size-3.5 text-muted-foreground" />
        {title}
      </h3>
      <div className="space-y-4 pl-1">{children}</div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="max-w-md">
        <Label className="text-sm">{label}</Label>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{hint}</p>
      </div>
      {children}
    </div>
  );
}

function Toggle({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="max-w-md">
        <Label className="flex items-center gap-2 text-sm">
          {label}
          {checked ? (
            <Check className="size-3.5 text-primary" />
          ) : (
            <X className="size-3.5 text-muted-foreground" />
          )}
        </Label>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{hint}</p>
      </div>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </div>
  );
}

/**
 * Where the weekly summary is posted, besides email.
 *
 * A webhook posts to a SPACE, not to a person, so this is the team nudge and not
 * a replacement for the personal reminder. Said on the screen, because somebody
 * configuring it will otherwise expect it to reach individuals.
 *
 * The URL is never read back from the server. The field is a REPLACE field: leave
 * it empty and whatever is stored stays, which is why it says so rather than
 * showing a row of dots that look editable.
 */
function ChatSection({
  settings,
  set,
}: {
  settings: Settings & { chatWebhookConfigured?: boolean };
  set: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
}) {
  const test = useTestChat();
  const [url, setUrl] = React.useState('');
  const [result, setResult] = React.useState<{ ok: boolean; text: string } | null>(null);

  const provider = settings.chatProvider ?? 'none';
  const stored = settings.chatWebhookConfigured ?? false;

  const run = async () => {
    setResult(null);
    try {
      await test.mutateAsync({ provider, url: url.trim() || undefined });
      setResult({ ok: true, text: 'Sent. Check the space.' });
    } catch (err) {
      setResult({ ok: false, text: err instanceof ApiError ? err.message : 'Could not reach the space.' });
    }
  };

  return (
    <Section title="Chat space">
      <p className="max-w-prose text-xs leading-relaxed text-muted-foreground">
        Google Chat, Slack and Microsoft Teams all accept an incoming webhook. Create one on
        the space you want the summary in, paste the URL here, and send a test before saving.
        A webhook posts to the space — the reminder to each individual still goes by email.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <Label className="w-40 shrink-0">Post to</Label>
        <Select value={provider} onValueChange={(v) => set('chatProvider', v as Settings['chatProvider'])}>
          <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Nowhere</SelectItem>
            <SelectItem value="google">Google Chat</SelectItem>
            <SelectItem value="slack">Slack</SelectItem>
            <SelectItem value="teams">Microsoft Teams</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {provider !== 'none' && (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="chat-webhook">Webhook URL</Label>
            <Input
              id="chat-webhook"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                // Into the draft as well, or Save posts everything EXCEPT this -
                // which is exactly what happened the first time it was used.
                set('chatWebhookUrl', e.target.value.trim());
              }}
              placeholder={stored ? 'A webhook is stored — type a new one to replace it' : 'https://chat.googleapis.com/v1/spaces/...'}
              autoComplete="off"
              spellCheck={false}
            />
            <p className="text-xs text-muted-foreground">
              {stored
                ? 'Stored and never shown again. Leave this empty to keep it.'
                : 'Nothing is stored yet, so nothing is posted.'}{' '}
              Saving an empty box changes nothing.
            </p>
          </div>

          <Toggle
            label="Post the weekly unsubmitted summary"
            hint="Once a week, the same list the reminder emails are built from: how many people are behind, and who."
            checked={settings.chatWeeklySummary ?? false}
            onChange={(v) => set('chatWeeklySummary', v)}
          />

          <div className="flex flex-wrap items-center gap-3">
            <Button hint={"Post a test message to the chat webhook"} variant="outline" onClick={() => void run()} disabled={test.isPending}>
              {test.isPending ? 'Sending…' : 'Send a test message'}
            </Button>
            {/* Tested against the typed URL when there is one, so a bad webhook
                is caught before it replaces a working one. */}
            {result && (
              <span className={`text-sm ${result.ok ? 'text-muted-foreground' : 'text-destructive'}`}>
                {result.text}
              </span>
            )}
          </div>

          {url.trim() !== '' && (
            <p className="text-xs text-muted-foreground">
              Remember to save — the test uses the URL above, saving is what stores it.
            </p>
          )}
        </>
      )}
    </Section>
  );
}
